import React from 'react';
import { Competence, Service } from '../../../../../../shared/types/config';

interface CompetenceModalProps {
  editingCompetence: Competence | null;
  competenceForm: Omit<Competence, 'id'>;
  setCompetenceForm: React.Dispatch<React.SetStateAction<Omit<Competence, 'id'>>>;
  services: Service[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const CompetenceModal: React.FC<CompetenceModalProps> = ({
  editingCompetence,
  competenceForm,
  setCompetenceForm,
  services,
  onClose,
  onSave,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2d4d]">
          <h2 className="text-base font-bold text-white">
            {editingCompetence ? 'Modifier la compétence' : 'Ajouter une compétence'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-[#1e2d4d] rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Nom *</label>
              <input
                type="text"
                value={competenceForm.nom}
                onChange={(e) => setCompetenceForm({ ...competenceForm, nom: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Type *</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={!competenceForm.is_specialite}
                    onChange={() => setCompetenceForm({ ...competenceForm, is_specialite: false })}
                    className="w-4 h-4"
                  />
                  Compétence normale
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    checked={competenceForm.is_specialite}
                    onChange={() => setCompetenceForm({ ...competenceForm, is_specialite: true })}
                    className="w-4 h-4"
                  />
                  Spécialité
                </label>
              </div>
            </div>
            <div>
              <label className={labelCls}>Service</label>
              <select
                value={competenceForm.id_service || ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, id_service: e.target.value ? parseInt(e.target.value) : null })}
                className={inputCls}
              >
                <option value="">Générale (tous services)</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>{service.nom}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">Laissez vide pour une compétence disponible pour tous les services</p>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea
                value={competenceForm.description || ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, description: e.target.value })}
                className={`${inputCls} resize-none`}
                rows={3}
                placeholder="Description de la compétence..."
              />
            </div>
            <div>
              <label className={labelCls}>
                Ordre d'affichage (optionnel)
                <span className="ml-2 text-[10px] text-slate-500">(ex: 1 = CA SAP, 2 = COND VSAV…)</span>
              </label>
              <input
                type="number"
                value={competenceForm.ordre ?? ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, ordre: e.target.value ? parseInt(e.target.value) : undefined })}
                className={inputCls}
                min="1"
                placeholder="Ex: 1, 2, 3..."
              />
              <p className="mt-1 text-xs text-slate-500">Triées par ordre croissant. Si vide, triées par nom.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#1e2d4d]">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
          >
            {editingCompetence ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetenceModal;
